import React, { useEffect, useState, useRef } from 'react';
import { Github, Twitter, Linkedin, Mail, ExternalLink, Twitch } from 'lucide-react';
import { mockData } from '../mock';
import './Portfolio.css';

const getFavoriteCards = (favorites) => {
  if (!favorites) return [];

  if (Array.isArray(favorites)) {
    return favorites.map((item, idx) => ({
      id: item.id || `fav-${idx}`,
      title: item.title || item.category || item.type || 'Favorite',
      icon: item.icon || '⭐',
      name: item.name,
      by: item.by,
      link: item.link,
    }));
  }

  const defaultTitles = {
    podcast: 'Podcast',
    podcasts: 'Podcast',
    manga: 'Manga',
    mangas: 'Manga',
    book: 'Book',
    books: 'Book',
    movie: 'Movie',
    movies: 'Movie',
    anime: 'Anime',
    animes: 'Anime',
    show: 'Show',
    shows: 'Show',
    game: 'Game',
    games: 'Game',
    music: 'Music',
  };

  const defaultIcons = {
    podcast: '🎙️',
    podcasts: '🎙️',
    manga: '📖',
    mangas: '📖',
    book: '📚',
    books: '📚',
  };

  const cards = [];

  Object.entries(favorites).forEach(([key, value]) => {
    if (!value) return;
    const items = Array.isArray(value) ? value : [value];
    const lowerKey = key.toLowerCase();
    const defaultTitle =
      defaultTitles[lowerKey] ||
      key.charAt(0).toUpperCase() + key.slice(1).replace(/s$/, '');
    const defaultIcon = defaultIcons[lowerKey] || '⭐';

    items.forEach((item, idx) => {
      if (!item) return;
      cards.push({
        id: item.id || `${key}-${idx}-${item.name}`,
        title: item.title || defaultTitle,
        icon: item.icon || defaultIcon,
        name: item.name,
        by: item.by,
        link: item.link,
      });
    });
  });

  return cards;
};

const Portfolio = () => {
  const [data, setData] = useState(null);
  const timelineRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [trackBounds, setTrackBounds] = useState({ top: 0, height: 0 });
  const targetProgressRef = useRef(0);
  const currentProgressRef = useRef(0);
  const animFrameRef = useRef(null);

  useEffect(() => {
    // Simulate API call with mock data
    setData(mockData);
  }, []);

  useEffect(() => {
    if (!data?.timeline) return;

    const updateMeasurements = () => {
      if (!timelineRef.current) return;
      const items = timelineRef.current.querySelectorAll('.timeline-item');
      if (!items || items.length === 0) return;

      const sectionRect = timelineRef.current.getBoundingClientRect();
      const firstDotRect = items[0].getBoundingClientRect();
      const lastDotRect = items[items.length - 1].getBoundingClientRect();

      const dotOffset = 13; // 0.5rem (8px) + 5px dot radius
      const top = firstDotRect.top - sectionRect.top + dotOffset;
      const bottom = lastDotRect.top - sectionRect.top + dotOffset;
      const height = Math.max(bottom - top, 0);

      setTrackBounds({ top, height });
    };

    const updateActiveDots = (progress) => {
      if (!timelineRef.current) return;
      const items = timelineRef.current.querySelectorAll('.timeline-item');
      if (!items || items.length === 0) return;

      const sectionRect = timelineRef.current.getBoundingClientRect();
      const firstDotTop = items[0].getBoundingClientRect().top + 13 - sectionRect.top;
      const lastDotTop = items[items.length - 1].getBoundingClientRect().top + 13 - sectionRect.top;
      const totalLength = lastDotTop - firstDotTop;

      items.forEach((item) => {
        const itemDotTop = item.getBoundingClientRect().top + 13 - sectionRect.top;
        const relativeY = itemDotTop - firstDotTop;
        const threshold = totalLength > 0 ? relativeY / totalLength : 0;

        if (progress >= threshold - 0.01 && progress > 0) {
          item.classList.add('timeline-active');
        } else {
          item.classList.remove('timeline-active');
        }
      });
    };

    const runInterpolationLoop = () => {
      const diff = targetProgressRef.current - currentProgressRef.current;
      if (Math.abs(diff) > 0.0005) {
        // Buttery-smooth exponential damping factor (0.12)
        currentProgressRef.current += diff * 0.12;
        setScrollProgress(currentProgressRef.current);
        updateActiveDots(currentProgressRef.current);
        animFrameRef.current = window.requestAnimationFrame(runInterpolationLoop);
      } else {
        currentProgressRef.current = targetProgressRef.current;
        setScrollProgress(targetProgressRef.current);
        updateActiveDots(targetProgressRef.current);
        animFrameRef.current = null;
      }
    };

    const handleScroll = () => {
      if (!timelineRef.current) return;
      const items = timelineRef.current.querySelectorAll('.timeline-item');
      if (!items || items.length === 0) return;

      const firstDotTop = items[0].getBoundingClientRect().top + 13;
      const lastDotTop = items[items.length - 1].getBoundingClientRect().top + 13;
      const totalLength = lastDotTop - firstDotTop;

      const focalPoint = window.innerHeight * 0.6;
      const distance = focalPoint - firstDotTop;
      const target = totalLength > 0 ? Math.min(Math.max(distance / totalLength, 0), 1) : 0;
      targetProgressRef.current = target;

      if (!animFrameRef.current) {
        animFrameRef.current = window.requestAnimationFrame(runInterpolationLoop);
      }
    };

    const onResize = () => {
      updateMeasurements();
      handleScroll();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', onResize);

    const timer = setTimeout(() => {
      updateMeasurements();
      handleScroll();
    }, 50);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', onResize);
      if (animFrameRef.current) {
        window.cancelAnimationFrame(animFrameRef.current);
      }
      clearTimeout(timer);
    };
  }, [data]);

  if (!data) return <div className="loading">Loading...</div>;

  return (
    <div className="portfolio-container">
      <div className="portfolio-content">
        {/* Hero Section */}
        <header className="hero-section">
          <h1 className="name">{data.name}</h1>
          {data.taglines.map((tagline) => <p className="tagline">{tagline}</p>)}
          
          {/* Social Links */}
          <div className="social-links">
            {data.social.github && (
              <a href={data.social.github} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="GitHub">
                <Github size={20} />
              </a>
            )}
            {data.social.twitter && (
              <a href={data.social.twitter} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Twitter">
                <Twitter size={20} />
              </a>
            )}
            {data.social.linkedin && (
              <a href={data.social.linkedin} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="LinkedIn">
                <Linkedin size={20} />
              </a>
            )}
            {data.social.email && (
              <a href={`mailto:${data.social.email}`} className="social-icon" aria-label="Email">
                <Mail size={20} />
              </a>
            )}
            {data.social.twitch && (
              <a href={data.social.twitch} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Twitch">
                <Twitch size={20} />
              </a>
            )}
          </div>
        </header>

        <div className="divider"></div>

        {/* Bio Section */}
        {/* <section className="bio-section">
          <p className="bio-text">{data.bio}</p>
        </section> */}

        {/* Timeline Section */}
        <section className="timeline-section" ref={timelineRef}>
          <h2 className="section-title">Journey</h2>
          
          {/* Animated Scroll Line Overlay */}
          <div
            className="timeline-track"
            style={{
              top: trackBounds.height > 0 ? `${trackBounds.top}px` : '4.5rem',
              height: trackBounds.height > 0 ? `${trackBounds.height}px` : 'calc(100% - 6rem)',
            }}
          >
            <div className="timeline-track-base" />
            <div
              className="timeline-track-fill"
              style={{
                height: `${scrollProgress * 100}%`,
              }}
            >
              <div
                className="timeline-track-glow-head"
                style={{
                  opacity: scrollProgress > 0 ? 1 : 0,
                }}
              />
            </div>
          </div>
          
          {data.timeline.map((item, index) => (
            <div key={index} className="timeline-item">
              <div className="timeline-header">
                <h3 className="timeline-year">{item.year}</h3>
              </div>
              
              <div className="timeline-content">
                {item.logo && (
                  <div className="company-logo-container">
                    {item.logo.startsWith('http') ? (
                      <img src={item.logo} alt={item.company} className="company-logo" />
                    ) : (
                      <div className="company-logo-text">{item.logo}</div>
                    )}
                  </div>
                )}
                
                <div className="timeline-details">
                  <h4 className="timeline-title">{item.title}</h4>
                  {item.company && <p className="timeline-company">{item.company}</p>}
                  {item.duration && <p className="timeline-duration">{item.duration}</p>}
                  <p className="timeline-description">{item.description}</p>
                  {item.description2 && <p className="timeline-description">{item.description2}</p>}
                  
                  {item.links && item.links.length > 0 && (
                    <div className="timeline-links">
                      {item.links.map((link, idx) => (
                        <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="timeline-link">
                          {link.text} <ExternalLink size={14} />
                        </a>
                      ))}
                    </div>
                  )}
                  
                  {item.images && item.images.length > 0 && (
                    <div className="timeline-images">
                      {item.images.map((img, idx) => (
                        <img key={idx} src={img} alt={`${item.title} screenshot ${idx + 1}`} className="timeline-image" />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Projects Section */}
        <section className="projects-section">
          <h2 className="section-title">Projects</h2>
          <p className="section-intro">Side projects and experiments worth mentioning:</p>
          
          <div className="projects-grid">
            {data.projects.map((project, index) => (
              <div key={index} className="project-card">
                <div className="project-header">
                  <h4 className="project-name">{project.name}</h4>
                  {project.stars > 0 && (
                    <span className="project-stars">★ {project.stars}</span>
                  )}
                </div>
                <p className="project-description">{project.description}</p>
                {project.tech && (
                  <div className="project-tech">
                    {project.tech.map((tech, idx) => (
                      <span key={idx} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                )}
                {project.link && (
                  <a href={project.link} target="_blank" rel="noopener noreferrer" className="project-link">
                    View on GitHub <ExternalLink size={14} />
                  </a>
                )}
                {project.installs && (
                  <p className="project-installs">{project.installs} installs</p>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Favorites Section */}
        {data.favorites && (
          <section className="favorites-section">
            <h2 className="section-title">Personal favorites</h2>
            <p className="section-intro">That inspire, entertain and educate me</p>
            
            <div className="favorites-grid">
              {getFavoriteCards(data.favorites).map((item) => {
                const cardContent = (
                  <>
                    {item.icon && <span className="favorite-icon">{item.icon}</span>}
                    {item.title && <h4 className="favorite-title">{item.title}</h4>}
                    <p className="favorite-name">{item.name}</p>
                    {item.by && (
                      <p className="favorite-by">
                        {item.by.toLowerCase().startsWith('by ') ? item.by : `by ${item.by}`}
                      </p>
                    )}
                  </>
                );

                return item.link ? (
                  <a
                    key={item.id}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="favorite-card"
                  >
                    {cardContent}
                  </a>
                ) : (
                  <div key={item.id} className="favorite-card">
                    {cardContent}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="portfolio-footer">
          <p className="note-to-self">
            <span className="note-label">Note to self</span> - "Always remember: You are entirely capable of figuring it out. Of squeezing everything you want out of this life. You are at the wheel. Never let go."
          </p>
        </footer>
      </div>
    </div>
  );
};

export default Portfolio;